import { BlueButton } from './buttons/BlueButton'
import { IosShare } from '@mui/icons-material'
import generatePDF, { Margin } from 'react-to-pdf'
import { Modal } from './Modal'
import { Box, Card, CircularProgress, Stack, Typography, type SxProps, type Theme } from '@mui/material'
import { useState, type ReactNode } from 'react'
import { toast } from 'react-toastify'

const delay = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

const styles: Record<string, SxProps<Theme>> = {
  container: {
    backgroundColor: 'var(--BaseTones-primitive-nuetral-50, rgba(238, 238, 239, 1))',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  section: {
    backgroundColor: 'var(--BaseTones-primitive-surface-100, rgba(255, 255, 255, 1))',
    padding: '32px 40px 40px 40px',
    borderRadius: '8px',
    border: '1px solid var(--Theme-primitive-primary-300, rgba(84, 153, 195, 1))',
    boxShadow: '0px 2px 7px -1px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08)), 0px 1px 0px 0px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08))'
  }
}

interface Props {
  title?: string
  pdfElementId: string
  cardTestId?: string
  exportButtonTestId?: string
  variant?: 'card' | 'plain'
  children: (forceExpanded: boolean | undefined) => ReactNode
}

export const PdfReportShell = ({ title, pdfElementId, cardTestId, exportButtonTestId, variant = 'card', children }: Props): JSX.Element => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [forceExpanded, setForceExpanded] = useState<boolean | undefined>(undefined)

  const exportPDF = async (): Promise<void> => {
    const target = document.getElementById(pdfElementId)
    if (target == null) return

    // The tab must open synchronously while the click's transient user activation is
    // still valid — opening it after generation gets popup-blocked, because the render
    // below outlives the activation window (~5s in Chrome, synchronous-only in Safari).
    const pdfWindow = window.open('', '_blank')
    if (pdfWindow != null) {
      pdfWindow.document.title = 'Generating PDF...'
      pdfWindow.document.body.textContent = 'Generating PDF, please wait...'
    }

    const originalWidth = target.style.width
    try {
      setIsGeneratingPdf(true)
      setForceExpanded(true)
      target.style.width = '1000px'
      // Wait long enough for React to re-render and MUI's collapse transition (~300ms) to complete.
      await delay(1000)
      const pdf = await generatePDF(() => target, {
        method: 'build',
        resolution: 3,
        page: { margin: Margin.MEDIUM, format: 'letter', orientation: 'landscape' },
        overrides: { canvas: { ignoreElements: (el) => el.hasAttribute('data-hide-in-pdf') } }
      })
      if (pdf != null) {
        const blobUrl = pdf.output('bloburl').toString()
        if (pdfWindow != null) {
          pdfWindow.location.href = blobUrl
        } else {
          // The pre-opened tab was blocked anyway; try a direct open as a last resort.
          window.open(blobUrl, '_blank')
        }
      } else {
        pdfWindow?.close()
        toast.error('Failed to generate PDF.')
      }
    } catch (error) {
      pdfWindow?.close()
      toast.error('An error occurred while generating the PDF.')
      console.error(error)
    } finally {
      target.style.width = originalWidth
      setForceExpanded(undefined)
      setIsGeneratingPdf(false)
    }
  }

  const header =
    <Stack direction='row' justifyContent={title != null ? 'space-between' : 'flex-end'} alignItems='center'>
      {title != null &&
        <Typography variant='h1'>{title}</Typography>
      }
      <BlueButton
        dataTestId={exportButtonTestId}
        onClick={exportPDF}
        endIcon={<IosShare />}
        disabled={isGeneratingPdf}
      >
        Export
      </BlueButton>
    </Stack>

  return (<>
    {variant === 'card'
      ? <Card sx={styles.container} data-testid={cardTestId}>
        <Stack gap='20px'>
          {header}

          <div id={pdfElementId}>
            <Stack sx={styles.section} gap={2}>
              {children(forceExpanded)}
            </Stack>
          </div>
        </Stack>
      </Card>
      : <Box data-testid={cardTestId}>
        <Stack gap='20px'>
          {header}

          <div id={pdfElementId}>
            {children(forceExpanded)}
          </div>
        </Stack>
      </Box>
    }

    {isGeneratingPdf && (
      <Modal
        data-hide-in-pdf
        open={true}
        title='Generating PDF'
        onConfirm={() => {}}
        onClose={() => {}}
        hideCancelButton={true}
        hideXtoClose={true}
        hideConfirmButton={true}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant='body1' mt={2}>
              Generating PDF, please wait...
            </Typography>
          </Box>
        }
      />
    )}
  </>)
}
